import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetGeoObjectsQuery } from '@shared/api';
import { apiErrorMessage } from '@entities/geodesy';

/** Blob → object URL на время жизни компонента (с отзывом при смене/размонтировании). */
export function useObjectUrl(blob: Blob | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

/** Сохранить Blob как файл с заданным именем. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Одно асинхронное действие экрана: флаг занятости + текст ошибки бэкенда.
 * `run` возвращает true при успехе — удобно для «сохранить и перейти дальше».
 */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async (
      fn: () => Promise<unknown>,
      fallback = 'Не удалось выполнить действие.',
    ): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        return true;
      } catch (err) {
        setError(apiErrorMessage(err) ?? fallback);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );
  /** То же, но с результатом: значение при успехе, `null` при ошибке. */
  const runValue = useCallback(
    async <T>(
      fn: () => Promise<T>,
      fallback = 'Не удалось выполнить действие.',
    ): Promise<T | null> => {
      setBusy(true);
      setError(null);
      try {
        return await fn();
      } catch (err) {
        setError(apiErrorMessage(err) ?? fallback);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );
  return { run, runValue, busy, error, setError };
}

/** Имена проектов/площадок/конструктивных элементов по id — из справочника `/objects`. */
export function useGeoObjectNames() {
  const { data } = useGetGeoObjectsQuery();
  return useMemo(() => {
    const projects = new Map((data?.projects ?? []).map((p) => [p.id, p.name]));
    const sites = new Map((data?.sites ?? []).map((s) => [s.id, s.name]));
    const elements = new Map(
      (data?.elements ?? []).map((el) => [
        el.id,
        el.code ? `${el.code} — ${el.title}` : el.title,
      ]),
    );
    return {
      project: (id: string | null | undefined) => (id ? (projects.get(id) ?? null) : null),
      site: (id: string | null | undefined) => (id ? (sites.get(id) ?? null) : null),
      element: (id: string | null | undefined) => (id ? (elements.get(id) ?? null) : null),
      projectsList: data?.projects ?? [],
      sitesList: data?.sites ?? [],
      elementsList: data?.elements ?? [],
    };
  }, [data]);
}

/** Черновик из `useDraft` — можно передать в подкомпонент формы целиком. */
export interface Draft<T> {
  draft: T;
  set: (next: T) => void;
  update: (fn: (prev: T) => T) => void;
  reset: () => void;
  dirty: boolean;
}

/**
 * Локальный черновик серверного значения. Пока черновик не трогали, он следует
 * за сервером (пришла свежая карточка — подхватили); тронули — держим правки,
 * `dirty` показывает расхождение с сервером. Сравнение — по JSON: значения тут
 * простые (настройки, списки точек), а ссылочное равенство после каждого ответа
 * API всё равно теряется.
 */
export function useDraft<T>(server: T): Draft<T> {
  const serverKey = JSON.stringify(server);
  const [state, setState] = useState<{ draft: T; base: string }>(() => ({
    draft: server,
    base: serverKey,
  }));

  let current = state;
  if (serverKey !== state.base) {
    const untouched = JSON.stringify(state.draft) === state.base;
    current = { draft: untouched ? server : state.draft, base: serverKey };
    setState(current);
  }

  const dirty = JSON.stringify(current.draft) !== serverKey;
  const set = useCallback((next: T) => setState((s) => ({ ...s, draft: next })), []);
  const update = useCallback(
    (fn: (prev: T) => T) => setState((s) => ({ ...s, draft: fn(s.draft) })),
    [],
  );
  const reset = useCallback(
    () => setState({ draft: server, base: serverKey }),
    [server, serverKey],
  );

  return { draft: current.draft, set, update, reset, dirty };
}
